@let investigation = sharedInvestigation();
@let incidentPhotoUrls = sharedIncidentPhotoUrls();
@let reportPhotoUrls = sharedReportPhotoUrls();

@if (isLoading()) {
  <app-pan-card [isPageContainer]="true" [pageContainerSize]="'lg'" class="text-center">
    <div class="w-full flex justify-center">
      <app-pan-spinner size="lg"></app-pan-spinner>
    </div>
  </app-pan-card>
}

@if (investigation) {
  <app-pan-card
    [isPageContainer]="true"
    [pageContainerSize]="'lg'"
    class="mb-10"
    [header]="investigation?.case ? 'Case ' + investigation.case.num + ': Instance ' + investigation.case.instance_num : ''"
  >
    <div rightActions class="flex gap-2">
      <app-share-email-modal
        panRole
        [roles]="['admin', 'branch-owner', 'branch-manager', 'market-manager', 'location-manager']"
        modalTitle="Share Link to Investigation"
        [locationIds]="[investigation.location.id]"
        [isSharing]="isSharing()"
        (share)="share($event)"
        searchType="locations"
      ></app-share-email-modal>
    </div>

    <section class="text-center mb-4 flex flex-col gap-2">
      <div>{{ investigation.location.name }} ({{ investigation.location.branch_name }})</div>

      <div class="text-sm italic">
        @if (isSideload()) {
          {{ dateOfInstanceInTimezone() | date: "MM/dd/yyyy" }}
        } @else {
          {{ dateOfInstanceInTimezone() | date: "MM/dd/yyyy hh:mm:ss a" }}
        }
      </div>

      <section class="flex-1 justify-center flex gap-2">
        @for (theftType of theftTypes(); track $index) {
          <app-pan-chip color="info">{{ theftType | titlecase }}</app-pan-chip>
        }
      </section>

      @if (theftTypes().length === 0) {
        <app-pan-chip color="info">No Theft Type</app-pan-chip>
      }
    </section>

    @if (investigation.comments) {
      <app-pan-section-header title="Comments" class="my-2"></app-pan-section-header>

      <app-comment userName="" [dateCreated]="investigation.date_shared" [text]="investigation.comments" mode="normal"> </app-comment>
    }

    @if (sharedVideoClips().length > 0 || sharedVideoLinksAndPasswords().length > 0) {
      <app-pan-section-header title="Video(s)" class="my-2"></app-pan-section-header>

      @if (sharedVideoClips().length > 0) {
        <div class="flex flex-wrap gap-2">
          @for (clipLink of sharedVideoClips(); track clipLink; let i = $index) {
            <app-pan-video class="w-full max-w-[640px]" [src]="clipLink" data-testid="shared-investigation-video"></app-pan-video>
          }
        </div>
      } @else {
        <div class="flex flex-row flex-wrap gap-2">
          @for (v of sharedVideoLinksAndPasswords(); track $index) {
            <div class="w-[200px] h-[200px] flex justify-center items-center bg-gray-200 cursor-pointer" (click)="openVideo(v, videoModal)">
              <mat-icon class="text-xl">play_circle</mat-icon>
            </div>

            <app-pan-modal #videoModal>
              <div header>Video #{{ $index + 1 }}</div>

              <div body class="text-left">
                @if (v.password) {
                  <div class="mt-1">
                    Click here to copy the password:
                    <a panButton size="sm" (click)="copyToClipboard($event, v.password)">{{ v.password }}</a>
                  </div>
                }

                <div class="mt-1">
                  Click here to navigate to the video recording:
                  <a panButton icon="open_in_new" size="sm" [href]="v.link" target="_blank" panModalClose></a>
                </div>
              </div>

              <div footer>
                <app-pan-button panModalClose>Close</app-pan-button>
              </div>
            </app-pan-modal>
          }
        </div>
      }
    }

    @if (sharedFileAttachments().length > 0) {
      <app-pan-section-header title="File Attachment(s)" class="my-2"></app-pan-section-header>

      <div class="max-h-[70vh] overflow-y-auto pr-1">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          @for (file of sharedFileAttachments(); track file.url) {
            @if (!file.hasThumbnail || brokenThumbnails().has(file.url)) {
              <a panButton [href]="file.downloadUrl" target="_blank" color="info" size="lg" icon="download">Download</a>
            } @else {
              <div class="flex flex-col border rounded overflow-hidden" [matTooltip]="file.name">
                <div class="relative w-full aspect-[16/9] bg-gray-200">
                  <img [src]="file.thumbnailUrl" (error)="markThumbnailBroken(file.url)" [alt]="file.name" class="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div class="flex items-center justify-between gap-1 p-2">
                  <span class="text-sm truncate">{{ file.name }}</span>
                  <a panButton [href]="file.downloadUrl" target="_blank" color="info" size="sm" icon="download" matTooltip="Download" aria-label="Download"></a>
                </div>
              </div>
            }
          }
        </div>
      </div>
    }

    @if (incidentPhotoUrls.length > 0) {
      <app-pan-section-header title="Photo(s)" class="my-2"></app-pan-section-header>

      <div class="flex flex-row flex-wrap items-center gap-2">
        @for (imageUrl of incidentPhotoUrls; track imageUrl) {
          <img [src]="imageUrl" class="max-w-full max-h-[50vh] block" alt="incident-photo" />
        }
      </div>
    }

    @if (reportPhotoUrls.length > 0) {
      <app-pan-section-header title="Transaction Reports" class="my-2"></app-pan-section-header>

      <div class="flex flex-row flex-wrap items-center gap-2">
        @for (imageUrl of reportPhotoUrls; track imageUrl) {
          <img [src]="imageUrl" class="max-w-full max-h-[50vh] block" alt="report-photo" />
        }
      </div>
    }

    @if (!isSideload() && timelineRows().length > 0) {
      <app-pan-section-header title="Transactions" class="my-2"></app-pan-section-header>

      <app-incident-timeline-table [rows]="timelineRows()"></app-incident-timeline-table>
    }

    @if (isDiscussionsEnabled()) {
      <div class="flex justify-center my-4" panRole [roles]="['admin', 'branch-owner', 'branch-manager', 'market-manager']">
        <app-discussion-thread type="investigation-report" [id]="reviewId()"></app-discussion-thread>
      </div>
    }

    @if (investigation?.is_self_service) {
      <app-pan-inline-alert
        panRole
        [roles]="['admin', 'branch-owner', 'branch-manager', 'market-manager']"
        type="primary"
        class="flex-1 my-2"
      >
        This incident was submitted by {{ investigation.actor }}
      </app-pan-inline-alert>
    }

    <app-theft-notice-alert class="my-2"></app-theft-notice-alert>
  </app-pan-card>
}
